import { RouterErrorBoundary } from "@/components/error-boundary/error-boundary";
import Loading from "@/components/loading/loading";
import { getMockAccountSetupEntryPath } from "@/data/source/mock/mock-app-account-setting-data";
import { isDebugMode, isMockAppAccountSetting } from "@/utils/environment";
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router";

// Layouts
import AppAccountSettingLayout from "@app/(auth-approved-patient)/app-account-setting/app-account-setting-layout";
import AdminHomeLayout from "@app/(auth_admin)/admin/home/admin-home-layout";
import HomeLayout from "@app/(auth_doctor)/home/home-layout";
import FindPasswordLayout from "@app/find-password/find-password-layout";
import InitPasswordLayout from "@app/init-password/init-password-layout";
// import SelfSignupLayout from "@app/self-signup/self-signup-layout";
import RootLayout from "./root-layout";

// Eager load - Login page
import LoginPage from "@app/login-view";

// 동적 임포트 에러 핸들링을 위한 래퍼 함수
// 배포 후 캐시 불일치 문제 해결: 모듈 로드 실패 시 재시도 후 에러 바운더리로 전달
const lazyWithErrorHandling = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> => {
  return lazy(async () => {
    let retryCount = 0;
    const maxRetries = 1;

    console.log("[LazyImport] lazyWithErrorHandling 시작");

    while (retryCount <= maxRetries) {
      try {
        console.log(`[LazyImport] importFn 시도 (retryCount: ${retryCount}/${maxRetries})`);
        const result = await importFn();
        console.log("[LazyImport] importFn 성공");
        return result;
      } catch (error) {
        window.location.reload();
        console.error("[LazyImport] 에러 발생:", {
          error,
          errorType: error?.constructor?.name,
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          retryCount,
          maxRetries,
        });

        const isChunkLoadError =
          error instanceof Error &&
          (error.message.includes("error loading dynamically imported module") || //chrome 오류
            error.message.includes("Unable to preload CSS") || //firefox 오류
            error.message.includes("TypeError: 'text/html' is not a valid JavaScript MIME type.")); //safari 오류

        console.log("[LazyImport] 에러 분석:", {
          isChunkLoadError,
          isErrorInstance: error instanceof Error,
          errorMessageIncludes:
            error instanceof Error
              ? {
                  "Failed to fetch dynamically imported module": error.message.includes(
                    "Failed to fetch dynamically imported module"
                  ),
                  "error loading dynamically imported module": error.message.includes(
                    "error loading dynamically imported module"
                  ),
                  "Loading chunk": error.message.includes("Loading chunk"),
                  "Loading CSS chunk": error.message.includes("Loading CSS chunk"),
                  "text/html": error.message.includes("text/html"),
                  MIME: error.message.includes("MIME"),
                }
              : null,
        });

        // Chunk load error인 경우 재시도
        if (isChunkLoadError && retryCount < maxRetries) {
          console.warn("[LazyImport] 모듈 로드 실패", {
            retry: retryCount + 1,
            maxRetries,
            error,
          });
          retryCount++;
          await new Promise((resolve) => setTimeout(resolve, 100));
          continue;
        }

        // 최종 실패 시 에러를 throw하여 RouterErrorBoundary에서 처리하도록 함
        console.error("[LazyImport] 모듈 로드 최종 실패 - 에러 바운더리로 전달:", {
          error,
          errorType: error?.constructor?.name,
          errorMessage: error instanceof Error ? error.message : String(error),
          isChunkLoadError,
          retryCount,
        });
        throw error;
      }
    }

    console.error(
      "[LazyImport] :: Unexpected error in lazyWithErrorHandling - while 루프를 벗어남",
      {
        retryCount,
        maxRetries,
      }
    );
    throw new Error("Unexpected error in lazyWithErrorHandling");
  });
};

// Lazy load - Doctor pages
const DoctorHome = lazyWithErrorHandling(() => import("@app/(auth_doctor)/home/home-view"));
const DoctorPasswordChange = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/(my-info)/password-change/password-change-view")
);
const DoctorAddPatient = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/add/patient/add-patient-view")
);
const DoctorPatientList = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/patient/patient-view")
);
const DoctorPatientDetail = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/patient/[id]/patient-detail-view")
);
const DoctorTreatmentDetail = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/patient/[id]/[treatmentId]/treatment-detail-view")
);
const DoctorAddTreatment = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/patient/[id]/add-treatment/add-treatment-view")
);
const DoctorMedicalDeviceInfo = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/medical-device-info/medical-device-info-view")
);
const DoctorActionLogs = lazyWithErrorHandling(
  () => import("@app/(auth_doctor)/home/action-logs/action-logs-view")
);

// Lazy load - Admin pages
const AdminHome = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/admin-home-view")
);
const AdminPasswordChange = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/(my-info)/password-change/password-change-view")
);
const AdminAddDoctor = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/add-doctor/add-doctor-view")
);
const AdminDoctorList = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/doctors/doctors-view")
);
const AdminDoctorDetail = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/doctors/[id]/doctor-detail-view")
);
const AdminHospitalList = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/hospitals/hospitals-view")
);
const AdminAddHospital = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/hospitals/add/add-hospital-view")
);
const AdminPatientList = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/patients/patient-list-view")
);
const AdminPatientDetail = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/patients/[id]/patient-detail-view")
);
const AdminTreatmentDetail = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/patients/[id]/[treatmentId]/treatment-detail-view")
);
const AdminNoticeList = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/notices/notices-view")
);
const AdminAddNotice = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/notices/add/add-notice-view")
);
const AdminNoticeDetail = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/notices/detail/[id]/notice-detail-view")
);
const AdminEditNotice = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/notices/edit/[id]/edit-notice-view")
);
const AdminLogs = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/logs/logs-view")
);
const AdminActionLogs = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/action-logs/action-logs-view")
);
const AdminMedicalDeviceInfo = lazyWithErrorHandling(
  () => import("@app/(auth_admin)/admin/home/medical-device-info/medical-device-info-view")
);

// Lazy load - Find Password pages
const FindPasswordRequestLink = lazyWithErrorHandling(
  () => import("@app/find-password/request-link/request-link-view")
);
// const FindPasswordCodeValidation = lazyWithErrorHandling(
//   () => import("@app/find-password/code-validation/code-validation-view")
// );
const FindPasswordRequestComplete = lazyWithErrorHandling(
  () => import("@app/find-password/request-complete/request-complete-view")
);
const FindPasswordNewPassword = lazyWithErrorHandling(
  () => import("@app/find-password/[token]/reset-password-view")
);

// Lazy load - Init Password page
const InitPassword = lazyWithErrorHandling(
  () => import("@app/init-password/[token]/init-password-view")
);

// Lazy load - App Account Setting pages
const AppAccountSettingView = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/app-account-setting-view")
);
const AppAccountSettingCheckName = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/check-name/check-name-view")
);
const AppAccountSettingConfirmInfo = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/confirm-info/confirm-info-view")
);
const AppAccountSettingSetup = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/setup-account/setup-account-view")
);
const AppAccountSettingComplete = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/complete/complete-view")
);
const AppAccountSettingExpired = lazyWithErrorHandling(
  () => import("@app/(auth-approved-patient)/app-account-setting/expired/expired-view")
);
const ParentalConsent = lazyWithErrorHandling(
  () =>
    import(
      "@app/(auth-approved-patient)/app-account-setting/parental-consent/parental-consent-view"
    )
);

// // Lazy load - Self Signup pages
// const SelfSignup = lazyWithErrorHandling(() => import("@app/self-signup/self-signup-view"));
// const SelfSignupNotAllowed = lazyWithErrorHandling(
//   () => import("@app/self-signup/not-allowed/not-allowed-view")
// );
// const SelfSignupAddPatient = lazyWithErrorHandling(
//   () => import("@app/self-signup/add/patient/add-patient-view")
// );
// const SelfSignupAddGuardian = lazyWithErrorHandling(
//   () => import("@app/self-signup/add/guardian/add-guardian-view")
// );
// const SelfSignupSummary = lazyWithErrorHandling(
//   () => import("@app/self-signup/add/summary/summary-view")
// );
// const SelfSignupComplete = lazyWithErrorHandling(
//   () => import("@app/self-signup/add/complete/complete-view")
// );
// const SelfSignupCancel = lazyWithErrorHandling(
//   () => import("@app/self-signup/add/cancel/cancel-view")
// );

// Lazy load - Medical Device Info page
const MedicalDeviceInfo = lazyWithErrorHandling(
  () => import("@app/medical-device-info/medical-device-info-view")
);

// Suspense wrapper
const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

const router = createBrowserRouter([
  //MARK:- Root Layout
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        index: true,
        element: <LoginPage />,
      },
      //MARK:- Medical Device Info page
      {
        path: "medical-device-info",
        element: (
          <SuspenseWrapper>
            <MedicalDeviceInfo />
          </SuspenseWrapper>
        ),
      },
      //MARK:- Home Layout
      {
        path: "home",
        element: <HomeLayout />,
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <DoctorHome />
              </SuspenseWrapper>
            ),
          },
          {
            path: "password-change",
            element: (
              <SuspenseWrapper>
                <DoctorPasswordChange />
              </SuspenseWrapper>
            ),
          },
          {
            path: "add/patient",
            element: (
              <SuspenseWrapper>
                <DoctorAddPatient />
              </SuspenseWrapper>
            ),
          },
          {
            path: "patient",
            element: (
              <SuspenseWrapper>
                <DoctorPatientList />
              </SuspenseWrapper>
            ),
          },
          {
            path: "patient/:id",
            element: (
              <SuspenseWrapper>
                <DoctorPatientDetail />
              </SuspenseWrapper>
            ),
          },
          {
            path: "patient/:id/add-treatment",
            element: (
              <SuspenseWrapper>
                <DoctorAddTreatment />
              </SuspenseWrapper>
            ),
          },
          {
            path: "patient/:id/:treatmentId",
            element: (
              <SuspenseWrapper>
                <DoctorTreatmentDetail />
              </SuspenseWrapper>
            ),
          },
          // DEV/QA 환경 전용 라우트 (PROD 빌드에서는 등록되지 않아 URL 직접 접근도 불가)
          ...(isDebugMode
            ? [
                {
                  path: "action-logs",
                  element: (
                    <SuspenseWrapper>
                      <DoctorActionLogs />
                    </SuspenseWrapper>
                  ),
                },
              ]
            : []),
          {
            path: "medical-device-info",
            element: (
              <SuspenseWrapper>
                <DoctorMedicalDeviceInfo />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      //MARK:- Admin Home Layout
      {
        path: "admin/home",
        element: <AdminHomeLayout />,
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <AdminHome />
              </SuspenseWrapper>
            ),
          },
          {
            path: "password-change",
            element: (
              <SuspenseWrapper>
                <AdminPasswordChange />
              </SuspenseWrapper>
            ),
          },
          {
            path: "add-doctor",
            element: (
              <SuspenseWrapper>
                <AdminAddDoctor />
              </SuspenseWrapper>
            ),
          },
          {
            path: "doctors",
            element: (
              <SuspenseWrapper>
                <AdminDoctorList />
              </SuspenseWrapper>
            ),
          },
          {
            path: "doctors/:id",
            element: (
              <SuspenseWrapper>
                <AdminDoctorDetail />
              </SuspenseWrapper>
            ),
          },
          {
            path: "hospitals",
            element: (
              <SuspenseWrapper>
                <AdminHospitalList />
              </SuspenseWrapper>
            ),
          },
          {
            path: "hospitals/add",
            element: (
              <SuspenseWrapper>
                <AdminAddHospital />
              </SuspenseWrapper>
            ),
          },
          {
            path: "patients",
            element: (
              <SuspenseWrapper>
                <AdminPatientList />
              </SuspenseWrapper>
            ),
          },
          {
            path: "patients/:id",
            element: (
              <SuspenseWrapper>
                <AdminPatientDetail />
              </SuspenseWrapper>
            ),
          },
          {
            path: "patients/:id/:treatmentId",
            element: (
              <SuspenseWrapper>
                <AdminTreatmentDetail />
              </SuspenseWrapper>
            ),
          },
          {
            path: "notices",
            element: (
              <SuspenseWrapper>
                <AdminNoticeList />
              </SuspenseWrapper>
            ),
          },
          {
            path: "notices/add",
            element: (
              <SuspenseWrapper>
                <AdminAddNotice />
              </SuspenseWrapper>
            ),
          },
          {
            path: "notices/detail/:id",
            element: (
              <SuspenseWrapper>
                <AdminNoticeDetail />
              </SuspenseWrapper>
            ),
          },
          {
            path: "notices/edit/:id",
            element: (
              <SuspenseWrapper>
                <AdminEditNotice />
              </SuspenseWrapper>
            ),
          },
          {
            path: "logs",
            element: (
              <SuspenseWrapper>
                <AdminLogs />
              </SuspenseWrapper>
            ),
          },
          // DEV/QA 환경 전용 라우트 (PROD 빌드에서는 등록되지 않아 URL 직접 접근도 불가)
          ...(isDebugMode
            ? [
                {
                  path: "action-logs",
                  element: (
                    <SuspenseWrapper>
                      <AdminActionLogs />
                    </SuspenseWrapper>
                  ),
                },
              ]
            : []),
          {
            path: "medical-device-info",
            element: (
              <SuspenseWrapper>
                <AdminMedicalDeviceInfo />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      //MARK:- Find Password Layout
      {
        path: "find-password",
        element: <FindPasswordLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/find-password/request-link" replace />,
          },
          {
            path: "request-link",
            element: (
              <SuspenseWrapper>
                <FindPasswordRequestLink />
              </SuspenseWrapper>
            ),
          },
          // {
          //   path: "code-validation",
          //   element: (
          //     <SuspenseWrapper>
          //       <FindPasswordCodeValidation />
          //     </SuspenseWrapper>
          //   ),
          // },
          {
            path: "request-complete",
            element: (
              <SuspenseWrapper>
                <FindPasswordRequestComplete />
              </SuspenseWrapper>
            ),
          },
          {
            path: ":token",
            element: (
              <SuspenseWrapper>
                <FindPasswordNewPassword />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      //MARK:- Init Password Layout
      {
        path: "init-password",
        element: <InitPasswordLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/" replace />,
          },
          {
            path: ":token",
            element: (
              <SuspenseWrapper>
                <InitPassword />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      //MARK:- App Account Setting Layout
      {
        path: "app-account-setting",
        element: <AppAccountSettingLayout />,
        children: [
          {
            index: true,
            // Mock 모드(npm run dev:mock)에서는 /app-account-setting 만 열어도 Mock 토큰 진입 화면으로 보낸다
            element: (
              <Navigate to={isMockAppAccountSetting ? getMockAccountSetupEntryPath() : "/"} replace />
            ),
          },
          {
            path: "check-name",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingCheckName />
              </SuspenseWrapper>
            ),
          },
          {
            path: "confirm-info",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingConfirmInfo />
              </SuspenseWrapper>
            ),
          },
          {
            path: "setup-account",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingSetup />
              </SuspenseWrapper>
            ),
          },
          {
            path: "complete",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingComplete />
              </SuspenseWrapper>
            ),
          },
          {
            path: "expired",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingExpired />
              </SuspenseWrapper>
            ),
          },
          {
            path: "parental-consent",
            element: (
              <SuspenseWrapper>
                <ParentalConsent />
              </SuspenseWrapper>
            ),
          },
          {
            path: ":token",
            element: (
              <SuspenseWrapper>
                <AppAccountSettingView />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      // //MARK:- Self Signup
      // {
      //   path: "self-signup",
      //   element: <SelfSignupLayout />,
      //   children: [
      //     {
      //       index: true,
      //       element: (
      //         <SuspenseWrapper>
      //           <SelfSignup />
      //         </SuspenseWrapper>
      //       ),
      //     },
      //     {
      //       path: "not-allowed",
      //       element: (
      //         <SuspenseWrapper>
      //           <SelfSignupNotAllowed />
      //         </SuspenseWrapper>
      //       ),
      //     },
      //     {
      //       path: "add/patient",
      //       element: (
      //         <SuspenseWrapper>
      //           <SelfSignupAddPatient />
      //         </SuspenseWrapper>
      //       ),
      //     },
      //     {
      //       path: "add/guardian",
      //       element: (
      //         <SuspenseWrapper>
      //           <SelfSignupAddGuardian />
      //         </SuspenseWrapper>
      //       ),
      //     },
      //     {
      //       path: "add/summary",
      //       element: (
      //         <SuspenseWrapper>
      //           <SelfSignupSummary />
      //         </SuspenseWrapper>
      //       ),
      //     },
      //     {
      //       path: "add/complete",
      //       element: (
      //         <SuspenseWrapper>
      //           <SelfSignupComplete />
      //         </SuspenseWrapper>
      //       ),
      //     },
      //     {
      //       path: "add/cancel",
      //       element: (
      //         <SuspenseWrapper>
      //           <SelfSignupCancel />
      //         </SuspenseWrapper>
      //       ),
      //     },
      //   ],
      // },
      //MARK:- Not Found page
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);

export default router;
